import "@xyflow/react/dist/style.css";

import {
  Background,
  Edge,
  Node,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import { DatePicker, Modal, notification } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, NumberInput, Textarea } from "@mantine/core";
import { IconMail } from "@tabler/icons-react";
import * as XLSX from "xlsx";
import dayjs, { Dayjs } from "dayjs";

import BankNode from "@/components/bankManagement/BankNode";
import MonthGroupNode from "@/components/bankManagement/MonthGroupNode";
import PayableNode from "@/components/bankManagement/PayableNode";
import ReceivableNode from "@/components/bankManagement/ReceivableNode";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useTranslation } from "react-i18next";

interface BankData {
  id: string;
  name: string;
  balance: number;
}

interface DetailData {
  id: string;
  amount: number;
  bankId: string;
  paymentDate?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  paymentMethod?: string | null;
  checkNumber?: string | null;
  checkClearanceDate?: string | null;
  handlingFee?: number | null;
  retentionAmount?: number | null;
  deductionAmount?: number | null;
  isSettled?: boolean | null;
  remarks?: string | null;
}

interface ReceivableData {
  id: string;
  name: string;
  requestedAmount: number;
  taxRate?: number;
  partnerId?: string;
  accountsReceivableDetail?: DetailData[];
}

interface PayableData {
  id: string;
  name: string;
  requestedAmount: number;
  partnerId?: string;
  accountsPayableDetail?: DetailData[];
}

interface PartnerData {
  id: string;
  name: string;
}

const COLUMN_POSITIONS = {
  receivable: 100,
  bank: 500,
  monthGroup: 900,
  payable: 1240,
};

const Y_START = 100;
const Y_SPACING = 150;
const CHILD_Y_SPACING = 80;
const BANK_GAP = 60;

const EXPAND_STORAGE_KEY_PREFIX = "bank-mindmap-expanded";

const getExpandStorageKey = () => {
  const companyId = localStorage.getItem("companyId") || "default";
  return `${EXPAND_STORAGE_KEY_PREFIX}::${companyId}`;
};

const loadExpandedMonths = (): Set<string> => {
  try {
    const raw = localStorage.getItem(getExpandStorageKey());
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};

const saveExpandedMonths = (set: Set<string>) => {
  try {
    localStorage.setItem(
      getExpandStorageKey(),
      JSON.stringify(Array.from(set))
    );
  } catch {
    // ignore quota errors
  }
};

const getEarliestPaymentDate = (
  details: DetailData[] | undefined,
  bankId: string
): Date | null => {
  if (!details) return null;
  let earliest: Date | null = null;
  for (const d of details) {
    if (d.bankId !== bankId || !d.paymentDate) continue;
    const dt = dayjs(d.paymentDate);
    if (!dt.isValid()) continue;
    const date = dt.toDate();
    if (!earliest || date < earliest) earliest = date;
  }
  return earliest;
};

const formatMonthLabel = (monthKey: string) => {
  if (monthKey === "unscheduled") return "未排定";
  const [y, m] = monthKey.split("-");
  return `${y}年${Number(m)}月`;
};

function BankMainPageContent() {
  const { t } = useTranslation();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editBankId, setEditBankId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    balance: 0,
    remarks: "",
  });

  const [rawBanks, setRawBanks] = useState<BankData[]>([]);
  const [rawReceivables, setRawReceivables] = useState<ReceivableData[]>([]);
  const [rawPayables, setRawPayables] = useState<PayableData[]>([]);
  const [rawPartners, setRawPartners] = useState<PartnerData[]>([]);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportBankId, setExportBankId] = useState<string | null>(null);
  const [exportBankName, setExportBankName] = useState("");
  const [exportDateRange, setExportDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);

  const [api, contextHolder] = notification.useNotification();

  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(
    () => loadExpandedMonths()
  );

  const nodeTypes = useMemo(
    () => ({
      bank: BankNode,
      receivable: ReceivableNode,
      payable: PayableNode,
      monthGroup: MonthGroupNode,
    }),
    []
  );

  const handleToggleMonth = useCallback((bankId: string, month: string) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      const key = `${bankId}::${month}`;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      saveExpandedMonths(next);
      return next;
    });
  }, []);

  const handleEditBank = useCallback((id: string, name: string, balance: number) => {
    setIsEditMode(true);
    setEditBankId(id);
    setFormData({ name, balance, remarks: "" });
    setIsModalOpen(true);
  }, []);

  const fetchAndBuild = useCallback(async () => {
    try {
      const [bankResult, receivableResult, payableResult, partnerResult] =
        await Promise.all([
          orpcClient.bank.list({}),
          orpcClient.accountsReceivable.list({}),
          orpcClient.accountsPayable.list({}),
          orpcClient.partner.list({}),
        ]);

      const banks: BankData[] = bankResult || [];
      const allReceivables: ReceivableData[] = receivableResult || [];
      const allPayables: PayableData[] = payableResult || [];
      const allPartners: PartnerData[] = partnerResult || [];

      setRawBanks(banks);
      setRawReceivables(allReceivables);
      setRawPayables(allPayables);
      setRawPartners(allPartners);

      const receivables: ReceivableData[] = allReceivables.filter(
        (r: ReceivableData) => {
          const paidAmount = (r.accountsReceivableDetail || []).reduce(
            (sum: number, d: DetailData) => sum + (d.amount || 0),
            0
          );
          const totalWithTax =
            (r.requestedAmount || 0) +
            (r.requestedAmount || 0) * ((r.taxRate || 0) / 100);
          return totalWithTax !== paidAmount;
        }
      );
      const payables: PayableData[] = allPayables.filter(
        (p: PayableData) => {
          const paidAmount = (p.accountsPayableDetail || []).reduce(
            (sum: number, d: DetailData) => sum + (d.amount || 0),
            0
          );
          return p.requestedAmount !== paidAmount;
        }
      );

      const pMap: Record<string, string> = {};
      allPartners.forEach((p: PartnerData) => {
        pMap[p.id] = p.name;
      });

      const allNodes: Node[] = [];
      const allEdges: Edge[] = [];

      let cursorY = Y_START;

      banks.forEach((bank) => {
        const bankReceivables = receivables.filter(
          (r) => (r.accountsReceivableDetail || []).some((d) => d.bankId === bank.id)
        );
        const bankPayables = payables.filter(
          (p) => (p.accountsPayableDetail || []).some((d) => d.bankId === bank.id)
        );

        const payableTotal = bankPayables.reduce(
          (sum, p) => sum + (p.requestedAmount || 0),
          0
        );

        const monthGroupsMap = new Map<string, PayableData[]>();
        bankPayables.forEach((p) => {
          const earliest = getEarliestPaymentDate(
            p.accountsPayableDetail,
            bank.id
          );
          const key = earliest ? dayjs(earliest).format("YYYY-MM") : "unscheduled";
          if (!monthGroupsMap.has(key)) monthGroupsMap.set(key, []);
          monthGroupsMap.get(key)!.push(p);
        });

        const monthGroups = Array.from(monthGroupsMap.entries())
          .sort(([a], [b]) => {
            if (a === "unscheduled") return 1;
            if (b === "unscheduled") return -1;
            return a.localeCompare(b);
          })
          .map(([month, items]) => ({
            month,
            items,
            expanded: expandedMonths.has(`${bank.id}::${month}`),
          }));

        let payableSideRows = 0;
        monthGroups.forEach((g) => {
          payableSideRows += 1;
          if (g.expanded) payableSideRows += g.items.length;
        });

        const rows = Math.max(
          bankReceivables.length,
          payableSideRows,
          1
        );
        const blockHeight = rows * CHILD_Y_SPACING;
        const bankY = cursorY + (blockHeight - CHILD_Y_SPACING) / 2;

        allNodes.push({
          id: `bank-${bank.id}`,
          type: "bank",
          position: { x: COLUMN_POSITIONS.bank, y: bankY },
          data: {
            id: bank.id,
            name: bank.name,
            balance: bank.balance,
            payableTotalAmount: payableTotal,
            onEdit: handleEditBank,
            onExport: handleExportBank,
          },
        });

        const receivableOffset =
          (rows - bankReceivables.length) * CHILD_Y_SPACING / 2;
        bankReceivables.forEach((receivable, rIndex) => {
          const rY = cursorY + receivableOffset + rIndex * CHILD_Y_SPACING;

          const paidAmount = (receivable.accountsReceivableDetail || [])
            .filter((d) => d.bankId === bank.id)
            .reduce((sum, d) => sum + (d.amount || 0), 0);

          const receivableTotalWithTax =
            (receivable.requestedAmount || 0) +
            (receivable.requestedAmount || 0) * ((receivable.taxRate || 0) / 100);

          allNodes.push({
            id: `receivable-${receivable.id}`,
            type: "receivable",
            position: { x: COLUMN_POSITIONS.receivable, y: rY },
            data: {
              id: receivable.id,
              name: receivable.name,
              totalAmount: receivableTotalWithTax,
              bankId: bank.id,
              partnerName: receivable.partnerId
                ? pMap[receivable.partnerId]
                : undefined,
            },
          });

          allEdges.push({
            id: `edge-receivable-${receivable.id}-bank-${bank.id}`,
            source: `receivable-${receivable.id}`,
            target: `bank-${bank.id}`,
            sourceHandle: "right",
            targetHandle: "left",
            style: { stroke: "#000000", strokeWidth: 2 },
            label: `已收: $${Math.round(paidAmount).toLocaleString()}`,
            labelStyle: { fill: "#000000", fontWeight: "bold" },
            animated: true,
          });
        });

        const payableOffset =
          (rows - payableSideRows) * CHILD_Y_SPACING / 2;
        let payableY = cursorY + payableOffset;

        monthGroups.forEach((group) => {
          const monthNodeId = `monthGroup-${bank.id}-${group.month}`;
          const monthY = payableY;

          const groupTotal = group.items.reduce(
            (sum, p) => sum + (p.requestedAmount || 0),
            0
          );
          const groupPaid = group.items.reduce((sum, p) => {
            const paid = (p.accountsPayableDetail || [])
              .filter((d) => d.bankId === bank.id)
              .reduce((s, d) => s + (d.amount || 0), 0);
            return sum + paid;
          }, 0);
          const groupUnpaid = Math.max(0, groupTotal - groupPaid);

          allNodes.push({
            id: monthNodeId,
            type: "monthGroup",
            position: { x: COLUMN_POSITIONS.monthGroup, y: monthY },
            data: {
              bankId: bank.id,
              month: group.month,
              label: formatMonthLabel(group.month),
              count: group.items.length,
              totalAmount: groupTotal,
              unpaidAmount: groupUnpaid,
              expanded: group.expanded,
              onToggle: handleToggleMonth,
            },
          });

          allEdges.push({
            id: `edge-bank-${bank.id}-${monthNodeId}`,
            source: `bank-${bank.id}`,
            target: monthNodeId,
            sourceHandle: "right",
            targetHandle: "left",
            style: { stroke: "#f59e0b", strokeWidth: 2 },
            label: `${group.items.length} 筆`,
            labelStyle: { fill: "#b45309", fontWeight: "bold" },
            animated: true,
          });

          payableY += CHILD_Y_SPACING;

          if (group.expanded) {
            group.items.forEach((payable) => {
              const paidAmount = (payable.accountsPayableDetail || [])
                .filter((d) => d.bankId === bank.id)
                .reduce((sum, d) => sum + (d.amount || 0), 0);

              allNodes.push({
                id: `payable-${payable.id}`,
                type: "payable",
                position: { x: COLUMN_POSITIONS.payable, y: payableY },
                data: {
                  id: payable.id,
                  name: payable.name,
                  totalAmount: payable.requestedAmount,
                  paidAmount,
                  bankId: bank.id,
                  partnerName: payable.partnerId
                    ? pMap[payable.partnerId]
                    : undefined,
                },
              });

              const payableLineColor =
                payable.requestedAmount < bank.balance ? "#ef4444" : "#000000";
              allEdges.push({
                id: `edge-${monthNodeId}-payable-${payable.id}`,
                source: monthNodeId,
                target: `payable-${payable.id}`,
                sourceHandle: "right",
                targetHandle: "left",
                style: { stroke: payableLineColor, strokeWidth: 2 },
                label: `已付: $${Math.round(paidAmount).toLocaleString()}`,
                labelStyle: { fill: payableLineColor, fontWeight: "bold" },
                animated: true,
              });

              payableY += CHILD_Y_SPACING;
            });
          }
        });

        cursorY += blockHeight + BANK_GAP;
      });

      setNodes(allNodes);
      setEdges(allEdges);
    } catch (error) {
      console.error("Error fetching data:", error);
      api.error({
        message: "Error",
        description: "Failed to fetch data",
      });
    }
  }, [expandedMonths]);

  useEffect(() => {
    fetchAndBuild();
  }, [fetchAndBuild]);

  const handleExportBank = useCallback((id: string, name: string) => {
    setExportBankId(id);
    setExportBankName(name);
    setExportDateRange([null, null]);
    setExportModalOpen(true);
  }, []);

  const handleExportConfirm = useCallback(() => {
    if (!exportBankId) return;
    const [startDate, endDate] = exportDateRange;
    const pMap: Record<string, string> = {};
    rawPartners.forEach((p) => { pMap[p.id] = p.name; });

    const inRange = (date: string | null | undefined) => {
      if (!date) return false;
      const d = dayjs(date);
      if (startDate && d.isBefore(startDate, "day")) return false;
      if (endDate && d.isAfter(endDate, "day")) return false;
      return true;
    };

    const beforeStart = (date: string | null | undefined) => {
      if (!startDate) return false;
      if (!date) return false;
      return dayjs(date).isBefore(startDate, "day");
    };

    type ExportRow = {
      "日期": string;
      "類型": string;
      "帳款名稱": string;
      "廠商": string;
      "收入金額": number | string;
      "支出金額": number | string;
      "餘額": number | string;
      "手續費": number | string;
      "保留款": number | string;
      "扣款": number | string;
      "發票號碼": string;
      "發票日期": string;
      "付款方式": string;
      "支票號碼": string;
      "支票兌現日": string;
      "是否結清": string;
      "備註": string;
    };

    const emptyRow = (): Omit<ExportRow, "日期" | "類型" | "帳款名稱" | "廠商" | "餘額"> => ({
      "收入金額": "",
      "支出金額": "",
      "手續費": "",
      "保留款": "",
      "扣款": "",
      "發票號碼": "",
      "發票日期": "",
      "付款方式": "",
      "支票號碼": "",
      "支票兌現日": "",
      "是否結清": "",
      "備註": "",
    });

    const exportBank = rawBanks.find((b) => b.id === exportBankId);
    const bankOriginalBalance = exportBank?.balance || 0;

    let priorReceivable = 0;
    let priorPayable = 0;
    rawReceivables.forEach((r) =>
      (r.accountsReceivableDetail || []).forEach((d) => {
        if (d.bankId === exportBankId && beforeStart(d.paymentDate)) {
          priorReceivable += Math.round(d.amount || 0);
        }
      })
    );
    rawPayables.forEach((p) =>
      (p.accountsPayableDetail || []).forEach((d) => {
        if (d.bankId === exportBankId && beforeStart(d.paymentDate)) {
          priorPayable += Math.round(d.amount || 0);
        }
      })
    );
    const previousBalance = startDate
      ? Math.round(bankOriginalBalance) + priorReceivable - priorPayable
      : 0;

    const receivableEntries = rawReceivables.flatMap((r) =>
      (r.accountsReceivableDetail || [])
        .filter((d) => d.bankId === exportBankId && inRange(d.paymentDate))
        .map((d) => ({
          date: d.paymentDate || "",
          type: "應收" as const,
          name: r.name,
          partner: r.partnerId ? (pMap[r.partnerId] || "") : "",
          detail: d,
        }))
    );

    const payableEntries = rawPayables.flatMap((p) =>
      (p.accountsPayableDetail || [])
        .filter((d) => d.bankId === exportBankId && inRange(d.paymentDate))
        .map((d) => ({
          date: d.paymentDate || "",
          type: "應付" as const,
          name: p.name,
          partner: p.partnerId ? (pMap[p.partnerId] || "") : "",
          detail: d,
        }))
    );

    const combined = [...receivableEntries, ...payableEntries].sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return dayjs(a.date).valueOf() - dayjs(b.date).valueOf();
    });

    let runningBalance = previousBalance;
    let inRangeReceivableTotal = 0;
    let inRangePayableTotal = 0;
    const dataRows: ExportRow[] = combined.map((entry) => {
      const d = entry.detail;
      const isReceivable = entry.type === "應收";
      const amount = Math.round(d.amount || 0);
      if (isReceivable) inRangeReceivableTotal += amount;
      else inRangePayableTotal += amount;
      runningBalance += isReceivable ? amount : -amount;
      return {
        "日期": entry.date,
        "類型": entry.type,
        "帳款名稱": entry.name,
        "廠商": entry.partner,
        "收入金額": isReceivable ? amount : "",
        "支出金額": isReceivable ? "" : amount,
        "餘額": Math.round(runningBalance),
        "手續費": Math.round(d.handlingFee ?? 0),
        "保留款": Math.round(d.retentionAmount ?? 0),
        "扣款": Math.round(d.deductionAmount ?? 0),
        "發票號碼": d.invoiceNumber || "",
        "發票日期": d.invoiceDate || "",
        "付款方式": d.paymentMethod || "",
        "支票號碼": d.checkNumber || "",
        "支票兌現日": d.checkClearanceDate || "",
        "是否結清": d.isSettled ? "是" : "否",
        "備註": d.remarks || "",
      };
    });

    const previousBalanceRow: ExportRow = {
      "日期": "",
      "類型": "",
      "帳款名稱": "前一天餘額",
      "廠商": "",
      "餘額": previousBalance,
      ...emptyRow(),
    };

    const totalReceivableThroughEnd = priorReceivable + inRangeReceivableTotal;
    const totalPayableThroughEnd = priorPayable + inRangePayableTotal;
    const finalBalance =
      Math.round(bankOriginalBalance) + totalReceivableThroughEnd - totalPayableThroughEnd;

    const totalBalanceRow: ExportRow = {
      "日期": "",
      "類型": "",
      "帳款名稱": "餘額",
      "廠商": "",
      "餘額": finalBalance,
      ...emptyRow(),
    };

    const allRows: ExportRow[] = [previousBalanceRow, ...dataRows, totalBalanceRow];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(allRows);
    const numericCols = ["收入金額", "支出金額", "餘額", "手續費", "保留款", "扣款"];
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    const headerToCol: Record<string, number> = {};
    for (let C = range.s.c; C <= range.e.c; C++) {
      const headerCell = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
      if (headerCell?.v != null) headerToCol[String(headerCell.v)] = C;
    }
    for (const colName of numericCols) {
      const C = headerToCol[colName];
      if (C === undefined) continue;
      for (let R = 1; R <= range.e.r; R++) {
        const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
        if (cell && typeof cell.v === "number") {
          cell.z = "#,##0";
        }
      }
    }
    XLSX.utils.book_append_sheet(wb, ws, "收付款紀錄");
    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `${exportBankName}_收付款紀錄_${dateStr}.xlsx`);
    setExportModalOpen(false);
  }, [exportBankId, exportBankName, exportDateRange, rawBanks, rawReceivables, rawPayables, rawPartners]);

  const handleOpenAddModal = () => {
    setIsEditMode(false);
    setEditBankId(null);
    setFormData({ name: "", balance: 0, remarks: "" });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditBankId(null);
    setFormData({ name: "", balance: 0, remarks: "" });
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      api.warning({
        message: t("bank.requiredField") || "請填寫必填欄位",
        description: t("bank.nameRequired") || "銀行名稱為必填",
        placement: "topLeft",
      });
      return;
    }

    try {
      if (isEditMode && editBankId) {
        await single(
          orpcClient.bank.update([
            {
              id: editBankId,
              name: formData.name,
              balance: Math.round(Number(formData.balance) || 0),
            },
          ])
        );
        api.success({
          message: t("bank.updateSuccess") || "更新成功",
          description: t("bank.bankUpdated") || "銀行資料已更新",
          placement: "topLeft",
        });
      } else {
        await single(
          orpcClient.bank.create([
            {
              name: formData.name,
              balance: Math.round(Number(formData.balance) || 0),
              remarks: formData.remarks || undefined,
            },
          ])
        );
        api.success({
          message: t("bank.addSuccess") || "新增成功",
          description: t("bank.bankAdded") || "銀行已新增",
          placement: "topLeft",
        });
      }
      handleCloseModal();
      fetchAndBuild();
    } catch (error) {
      api.error({
        message: isEditMode
          ? t("bank.updateFailed") || "更新失敗"
          : t("bank.addFailed") || "新增失敗",
        description: (error as any)?.message || "操作時發生錯誤",
        placement: "topLeft",
      });
    }
  };

  return (
    <div className="h-screen">
      {contextHolder}
      <div
        className="flex items-center justify-between px-4 py-2"
        style={{ backgroundColor: "#ECECEC" }}
      >
        <div className="text-xl font-bold">
          {t("bankManagement.title") || "銀行管理"}
        </div>
        <Button
          variant="default"
          style={{ minWidth: "3rem" }}
          radius="xl"
          onClick={handleOpenAddModal}
        >
          {t("bank.addBank") || "新增銀行"}
        </Button>
      </div>

      <div style={{ height: "calc(100vh - 150px)" }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.5 }}
          style={{ backgroundColor: "#F7F9FB" }}
        >
          <Background />
        </ReactFlow>
      </div>

      <Modal
        open={exportModalOpen}
        width={460}
        title={`匯出「${exportBankName}」收付款紀錄`}
        onCancel={() => setExportModalOpen(false)}
        onOk={handleExportConfirm}
        okText="匯出 Excel"
        cancelText="取消"
      >
        <div className="py-4">
          <div style={{ color: "#6F6F6F", marginBottom: "8px" }}>選擇日期範圍（留空則匯出全部）</div>
          <DatePicker.RangePicker
            style={{ width: "100%" }}
            value={exportDateRange}
            onChange={(dates) => setExportDateRange(dates ? [dates[0], dates[1]] : [null, null])}
          />
        </div>
      </Modal>

      <Modal
        open={isModalOpen}
        width={600}
        title={isEditMode ? (t("bank.editBank") || "編輯銀行") : (t("bank.addBank") || "新增銀行")}
        onCancel={handleCloseModal}
        onOk={handleSubmit}
        okText={t("bank.confirm") || "確認"}
        cancelText={t("bank.cancel") || "取消"}
      >
        <div className="p-4">
          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>
              {t("bank.bankName") || "銀行名稱"}＊
            </div>
            <Input
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder={t("bank.enterBankName") || "請輸入銀行名稱"}
              leftSection={<IconMail size={16} />}
            />
          </div>
          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>
              {t("bank.balance") || "餘額"}＊
            </div>
            <NumberInput
              value={formData.balance}
              onChange={(val) =>
                setFormData({ ...formData, balance: Number(val) || 0 })
              }
              placeholder={t("bank.enterBalance") || "請輸入餘額"}
              thousandSeparator=","
            />
          </div>
          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>
              {t("bank.remarks") || "備註"}
            </div>
            <Textarea
              value={formData.remarks}
              onChange={(e) =>
                setFormData({ ...formData, remarks: e.target.value })
              }
              placeholder={t("bank.enterRemarks") || "請輸入備註"}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

function BankMainPage() {
  return (
    <ReactFlowProvider>
      <BankMainPageContent />
    </ReactFlowProvider>
  );
}

export default BankMainPage;
